(() => {
  if (window.__freeGiftCampaignsInit) return;

  const dataEl = document.getElementById('free-gift-campaigns-data');
  if (!dataEl) return;

  let config = null;
  try {
    config = JSON.parse(dataEl.textContent || '{}');
  } catch (e) {
    console.error('[free-gift-campaigns] invalid configuration', e);
    return;
  }

  const GIFT_PROPERTY = '__free_gift';
  const ATTR_PREFIX = '__fg_';
  const INTERNAL_HEADER = 'X-Free-Gift-Internal';
  const CART_WRITE_RE = /\/cart\/(add|change|update|clear)(\.js)?(\?|$)/;
  const CART_LOCK_RE = /\/cart\/(change|update)(\.js)?(\?|$)/;
  const COLLECTION_PAGE_SIZE = 250;
  const MAX_COLLECTION_PAGES = 6;
  const ATTR_SEPARATOR = '|';
  const FAILED_RETRY_MS = 60000;

  const toId = (value) => (value == null ? '' : String(value).trim());

  const normalize = (raw) => {
    if (!raw || !raw.id) return null;

    const giftItems = (raw.giftItems || [])
      .map((gift) => ({
        variantId: toId(gift.variantId),
        quantity: Math.max(1, parseInt(gift.quantity, 10) || 1),
      }))
      .filter((gift) => /^\d+$/.test(gift.variantId));

    if (!giftItems.length) return null;

    const giftVariantIds = new Set(
      (raw.giftVariantIds || []).map(toId).concat(giftItems.map((gift) => gift.variantId))
    );

    return {
      id: toId(raw.id),
      label: raw.label || toId(raw.id),
      codes: (raw.codes || []).map((code) => toId(code).toUpperCase()).filter(Boolean),
      rememberCodeFromUrl: raw.rememberCodeFromUrl !== false,
      triggerScope: raw.triggerScope === 'all' ? 'all' : 'products',
      triggerProductIds: new Set((raw.triggerProductIds || []).map(toId)),
      triggerCollections: (raw.triggerCollections || []).map(toId).filter(Boolean),
      requireSubscription: !!raw.requireSubscription,
      minimumType: raw.minimumType || 'none',
      minimumValue: Number(raw.minimumValue) || 0,
      giftItems,
      giftVariantIds,
      lockGift: raw.lockGift !== false,
    };
  };

  const campaigns = (config.campaigns || []).map(normalize).filter(Boolean);
  if (!campaigns.length) return;

  window.__freeGiftCampaignsInit = true;

  const DEBUG = !!config.debug;
  const REMOVE_ORPHANS = !!config.removeOrphanGifts;
  const campaignsById = new Map(campaigns.map((campaign) => [campaign.id, campaign]));
  const campaignsByLabel = new Map();
  const failedVariants = new Map();

  campaigns.forEach((campaign) => {
    const key = campaign.label.toUpperCase();
    if (campaignsByLabel.has(key)) {
      console.warn(
        `[free-gift-campaigns] duplicate campaign name "${campaign.label}" — gift lines cannot be attributed reliably, rename one of them`
      );
    }
    campaignsByLabel.set(key, campaign.id);
  });

  const log = (...args) => {
    if (DEBUG) console.log('[free-gift-campaigns]', ...args);
  };

  const markVariantFailed = (variantId, response) => {
    failedVariants.set(toId(variantId), Date.now());
    console.error('[free-gift-campaigns] gift add failed', variantId, response);
  };

  const isVariantFailed = (variantId) => {
    const id = toId(variantId);
    const failedAt = failedVariants.get(id);
    if (failedAt == null) return false;
    if (Date.now() - failedAt < FAILED_RETRY_MS) return true;
    failedVariants.delete(id);
    return false;
  };

  const collectionCache = new Map();

  const loadCollection = (handle) => {
    if (collectionCache.has(handle)) return collectionCache.get(handle);

    const storageKey = `fg:collection:${handle}`;
    let cached = null;
    try {
      cached = sessionStorage.getItem(storageKey);
    } catch (_) {}

    if (cached) {
      try {
        const promise = Promise.resolve(new Set(JSON.parse(cached)));
        collectionCache.set(handle, promise);
        return promise;
      } catch (_) {}
    }

    const promise = (async () => {
      const ids = [];
      for (let page = 1; page <= MAX_COLLECTION_PAGES; page += 1) {
        const url = `/collections/${encodeURIComponent(handle)}/products.json?limit=${COLLECTION_PAGE_SIZE}&page=${page}`;
        const response = await fetch(url, { credentials: 'same-origin' });
        if (!response.ok) break;
        const payload = await response.json();
        const products = (payload && payload.products) || [];
        products.forEach((product) => ids.push(String(product.id)));
        if (products.length < COLLECTION_PAGE_SIZE) break;
      }
      try {
        sessionStorage.setItem(storageKey, JSON.stringify(ids));
      } catch (_) {}
      return new Set(ids);
    })().catch((e) => {
      console.error(`[free-gift-campaigns] failed to resolve collection "${handle}"`, e);
      collectionCache.delete(handle);
      return new Set();
    });

    collectionCache.set(handle, promise);
    return promise;
  };

  const resolveCollections = async () => {
    const pending = campaigns.filter(
      (campaign) => campaign.triggerCollections.length && !campaign.collectionsResolved
    );
    if (!pending.length) return;

    await Promise.all(
      pending.map(async (campaign) => {
        const sets = await Promise.all(campaign.triggerCollections.map(loadCollection));
        sets.forEach((set) => set.forEach((id) => campaign.triggerProductIds.add(id)));
        campaign.collectionsResolved = true;
        log(`collections resolved for "${campaign.label}"`, campaign.triggerProductIds.size);
      })
    );
  };

  const getCart = () => fetch('/cart.js', { credentials: 'same-origin' }).then((r) => r.json());

  const internalPost = (url, body) =>
    fetch(url, {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        [INTERNAL_HEADER]: '1',
      },
      body: JSON.stringify(body),
    }).then((r) => r.json());

  const notifyCart = async () => {
    const events = [
      'dispatch:cart-drawer:refresh',
      'dispatch:upcart--refresh-cart',
      'cart:refresh',
      'cart:updated',
    ];
    events.forEach((name) => {
      try {
        document.dispatchEvent(new CustomEvent(name));
      } catch (_) {}
      try {
        window.dispatchEvent(new CustomEvent(name));
      } catch (_) {}
    });
    try {
      if (typeof window.upcartRefreshCart === 'function') await window.upcartRefreshCart();
    } catch (e) {
      console.error('[free-gift-campaigns] upcartRefreshCart failed', e);
    }
  };

  const codeInUrl = (campaign) => {
    if (!campaign.codes.length) return null;
    try {
      const path = (location.pathname || '').toLowerCase();
      const fromPath = campaign.codes.find((code) => path.includes(`/discount/${code.toLowerCase()}`));
      if (fromPath) return fromPath;

      const params = new URLSearchParams(location.search);
      for (const value of params.values()) {
        const upper = toId(value).toUpperCase();
        if (campaign.codes.indexOf(upper) !== -1) return upper;
      }
    } catch (_) {}
    return null;
  };

  const cartAttribute = (cart, campaign) =>
    (cart && cart.attributes && cart.attributes[ATTR_PREFIX + campaign.id]) || null;

  const appliedCodes = (cart) =>
    new Set(
      (cart.discount_codes || [])
        .map((discount) => toId(discount && discount.code).toUpperCase())
        .filter(Boolean)
    );

  const rememberedCode = (cart, campaign) => {
    const raw = toId(cartAttribute(cart, campaign));
    if (!raw) return null;
    const index = raw.lastIndexOf(ATTR_SEPARATOR);
    const code = (index === -1 ? raw : raw.slice(0, index)).toUpperCase();
    const at = index === -1 ? 0 : Number(raw.slice(index + 1)) || 0;
    if (campaign.codes.indexOf(code) === -1) return null;
    return { code, at };
  };

  const fallbackOwnerId = (cart, applied) => {
    if (applied.size) return null;
    let best = null;
    campaigns.forEach((campaign) => {
      if (!campaign.codes.length) return;
      const remembered = rememberedCode(cart, campaign);
      if (!remembered) return;
      if (!best || remembered.at > best.at) best = { id: campaign.id, at: remembered.at };
    });
    return best ? best.id : null;
  };

  const codeActive = (campaign, applied, fallbackId) => {
    if (!campaign.codes.length) return true;
    if (campaign.codes.some((code) => applied.has(code))) return true;
    return fallbackId === campaign.id;
  };

  const ownerCampaignId = (item) => {
    const properties = item && item.properties;
    if (!properties) return null;
    const value = toId(properties[GIFT_PROPERTY]);
    if (!value) return null;
    if (campaignsById.has(value)) return value;
    const upper = value.toUpperCase();
    if (campaignsByLabel.has(upper)) return campaignsByLabel.get(upper);
    const owner = campaigns.find((campaign) => campaign.codes.indexOf(upper) !== -1);
    return owner ? owner.id : '__orphan__';
  };

  const lineTotal = (item) =>
    Number(item.final_line_price != null ? item.final_line_price : item.line_price) || 0;

  const hasSellingPlan = (item) =>
    !!(item && (item.selling_plan_allocation || item.selling_plan_allocation_id));

  const isTriggerLine = (item, campaign) => {
    if (campaign.requireSubscription && !hasSellingPlan(item)) return false;
    if (campaign.triggerScope === 'all') return true;
    return campaign.triggerProductIds.has(toId(item.product_id));
  };

  const evaluate = (cart) => {
    const items = cart.items || [];
    const owners = items.map(ownerCampaignId);
    const regularItems = items.filter((_, index) => owners[index] === null);
    const cartSubtotal = regularItems.reduce((total, item) => total + lineTotal(item), 0);
    const applied = appliedCodes(cart);
    const fallbackId = fallbackOwnerId(cart, applied);

    const states = campaigns.map((campaign) => {
      const triggers = regularItems.filter((item) => isTriggerLine(item, campaign));
      const triggerSubtotal = triggers.reduce((total, item) => total + lineTotal(item), 0);
      const triggerQuantity = triggers.reduce((total, item) => total + (item.quantity || 0), 0);

      let minimumMet = true;
      if (campaign.minimumType === 'subtotal') minimumMet = cartSubtotal >= campaign.minimumValue;
      else if (campaign.minimumType === 'trigger_subtotal') minimumMet = triggerSubtotal >= campaign.minimumValue;
      else if (campaign.minimumType === 'trigger_quantity') minimumMet = triggerQuantity >= campaign.minimumValue;

      const codeOk = codeActive(campaign, applied, fallbackId);
      const qualifies = codeOk && triggers.length > 0 && minimumMet;

      return {
        campaign,
        qualifies,
        codeOk,
        minimumMet,
        triggers,
        triggerSubtotal,
        triggerQuantity,
        lines: items.filter((_, index) => owners[index] === campaign.id),
      };
    });

    const orphanLines = REMOVE_ORPHANS
      ? items.filter((_, index) => owners[index] === '__orphan__')
      : [];

    return { states, orphanLines, appliedCodes: applied, fallbackId, cartSubtotal };
  };

  const assignGiftOwners = (states) => {
    const owners = new Map();
    const qualifying = states.filter((state) => state.qualifies);

    qualifying.forEach(({ campaign, lines }) => {
      lines.forEach((line) => {
        const variantId = toId(line.variant_id);
        if (owners.has(variantId) || !(line.quantity > 0)) return;
        if (campaign.giftItems.some((gift) => gift.variantId === variantId)) owners.set(variantId, campaign.id);
      });
    });

    qualifying.forEach(({ campaign }) => {
      campaign.giftItems.forEach((gift) => {
        if (!owners.has(gift.variantId)) owners.set(gift.variantId, campaign.id);
      });
    });

    return owners;
  };

  const reconcile = ({ states, orphanLines }) => {
    const updates = {};
    const additions = [];
    let currentAdditions = null;
    const giftOwners = assignGiftOwners(states);

    states.forEach(({ campaign, qualifies, lines }) => {
      const desired = new Map();
      if (qualifies) {
        campaign.giftItems.forEach((gift) => {
          if (giftOwners.get(gift.variantId) === campaign.id) desired.set(gift.variantId, gift.quantity);
        });
      }

      const satisfied = new Set();
      lines.forEach((line) => {
        const variantId = toId(line.variant_id);
        const wanted = desired.has(variantId) && !satisfied.has(variantId) ? desired.get(variantId) : 0;
        if (wanted > 0) satisfied.add(variantId);
        if (line.quantity !== wanted) updates[line.key] = wanted;
      });

      currentAdditions = null;
      desired.forEach((quantity, variantId) => {
        if (satisfied.has(variantId)) return;
        if (isVariantFailed(variantId)) return;
        if (!currentAdditions) {
          currentAdditions = { campaign, items: [] };
          additions.push(currentAdditions);
        }
        currentAdditions.items.push({
          id: Number(variantId),
          quantity,
          properties: { [GIFT_PROPERTY]: campaign.label },
        });
      });
    });

    orphanLines.forEach((line) => {
      if (line.quantity !== 0) updates[line.key] = 0;
    });

    return { updates, additions };
  };

  const syncCodeAttributes = (cart) => {
    const applied = appliedCodes(cart);
    const attributes = {};

    campaigns.forEach((campaign) => {
      if (!campaign.codes.length) return;

      const key = ATTR_PREFIX + campaign.id;
      const stored = cartAttribute(cart, campaign);

      if (campaign.codes.some((code) => applied.has(code))) {
        if (stored) attributes[key] = '';
        return;
      }

      if (stored && (applied.size || !rememberedCode(cart, campaign) || !campaign.rememberCodeFromUrl)) {
        attributes[key] = '';
        return;
      }

      if (stored || !campaign.rememberCodeFromUrl) return;

      const code = codeInUrl(campaign);
      if (code) attributes[key] = code + ATTR_SEPARATOR + Date.now();
    });

    return attributes;
  };

  const addGiftItems = async (items) => {
    const isError = (response) => !!(response && response.status && Number(response.status) >= 400);

    const response = await internalPost('/cart/add.js', { items });
    if (!isError(response)) return true;

    if (items.length === 1) {
      markVariantFailed(items[0].id, response);
      return false;
    }

    let added = false;
    for (const item of items) {
      const single = await internalPost('/cart/add.js', { items: [item] });
      if (isError(single)) markVariantFailed(item.id, single);
      else added = true;
    }
    return added;
  };

  const runSync = async () => {
    let cart = await getCart();

    const attributes = syncCodeAttributes(cart);
    if (Object.keys(attributes).length) {
      log('updating remembered discount codes', attributes);
      cart = await internalPost('/cart/update.js', { attributes });
    }

    await resolveCollections();

    const { updates, additions } = reconcile(evaluate(cart));
    let changed = false;

    if (Object.keys(updates).length) {
      log('updating gift lines', updates);
      await internalPost('/cart/update.js', { updates });
      changed = true;
    }

    for (const group of additions) {
      log(`adding gift lines for "${group.campaign.label}"`, group.items);
      if (await addGiftItems(group.items)) changed = true;
    }

    if (changed) await notifyCart();
    return changed;
  };

  let syncChain = Promise.resolve(false);
  const sync = () => {
    syncChain = syncChain
      .then(runSync)
      .catch((e) => {
        console.error('[free-gift-campaigns] sync failed', e);
        return false;
      });
    return syncChain;
  };

  const extractPayload = (body) => {
    try {
      if (!body) return null;
      if (typeof body === 'string') {
        try {
          return JSON.parse(body);
        } catch (_) {
          const params = new URLSearchParams(body);
          const out = {};
          params.forEach((value, key) => {
            out[key] = value;
          });
          return Object.keys(out).length ? out : null;
        }
      }
      if (body instanceof FormData) {
        const out = {};
        body.forEach((value, key) => {
          out[key] = value;
        });
        return out;
      }
      if (typeof body === 'object') return body;
    } catch (_) {}
    return null;
  };

  const hasInternalHeader = (headers) => {
    try {
      if (headers instanceof Headers) return headers.has(INTERNAL_HEADER);
      if (Array.isArray(headers)) {
        return headers.some((entry) => entry && String(entry[0]).toLowerCase() === INTERNAL_HEADER.toLowerCase());
      }
      if (headers && typeof headers === 'object') {
        return Object.keys(headers).some((key) => key.toLowerCase() === INTERNAL_HEADER.toLowerCase());
      }
    } catch (_) {}
    return false;
  };

  const lockedQuantities = (cart) => {
    const locked = new Map();
    const { states } = evaluate(cart);
    states.forEach(({ campaign, qualifies, lines }) => {
      if (!campaign.lockGift || !qualifies) return;
      lines.forEach((line) => locked.set(line.key, line.quantity));
    });
    return locked;
  };

  const isChangeBlocked = (payload, cart) => {
    if (!payload) return false;

    const locked = lockedQuantities(cart);
    if (!locked.size) return false;

    const items = cart.items || [];
    const byKey = {};
    const byVariant = {};
    items.forEach((item) => {
      byKey[item.key] = item;
      const variantId = toId(item.variant_id);
      if (!(variantId in byVariant)) byVariant[variantId] = item;
    });

    const blocks = (line, quantity) => {
      if (!line || !locked.has(line.key)) return false;
      return Number(quantity) !== Number(locked.get(line.key));
    };

    if (payload.id != null && payload.quantity != null) {
      const target = byKey[payload.id] || byVariant[toId(payload.id)];
      if (blocks(target, payload.quantity)) return true;
    }

    if (payload.line != null && payload.quantity != null) {
      const target = items[Number(payload.line) - 1];
      if (blocks(target, payload.quantity)) return true;
    }

    if (payload.updates && typeof payload.updates === 'object') {
      if (Array.isArray(payload.updates)) {
        for (let index = 0; index < payload.updates.length; index += 1) {
          if (blocks(items[index], payload.updates[index])) return true;
        }
      } else {
        for (const [key, value] of Object.entries(payload.updates)) {
          const target = byKey[key] || byVariant[toId(key)];
          if (blocks(target, value)) return true;
        }
      }
    }

    return false;
  };

  const cartResponse = (cart) =>
    new Response(JSON.stringify(cart), {
      status: 200,
      headers: { 'Content-Type': 'application/json' },
    });

  const origFetch = window.fetch.bind(window);
  window.fetch = async (input, init) => {
    try {
      const url = typeof input === 'string' ? input : (input && input.url) || '';
      const method = ((init && init.method) || (input && input.method) || 'GET').toUpperCase();
      const internal = hasInternalHeader((init && init.headers) || {});

      if (!internal && method === 'POST' && CART_LOCK_RE.test(url)) {
        const payload = extractPayload(init && init.body);
        if (payload) {
          const cart = await getCart();
          if (isChangeBlocked(payload, cart)) {
            log('blocked cart change on a locked gift line', payload);
            await notifyCart();
            return cartResponse(cart);
          }
        }
      }

      const response = await origFetch(input, init);
      if (!internal && method === 'POST' && CART_WRITE_RE.test(url)) {
        const changed = await sync();
        if (changed && CART_LOCK_RE.test(url)) return cartResponse(await getCart());
      }
      return response;
    } catch (_) {
      return origFetch(input, init);
    }
  };

  const XHRProto = window.XMLHttpRequest && window.XMLHttpRequest.prototype;
  if (XHRProto && !XHRProto.__freeGiftPatched) {
    XHRProto.__freeGiftPatched = true;
    const origOpen = XHRProto.open;
    const origSend = XHRProto.send;
    const origSetHeader = XHRProto.setRequestHeader;

    XHRProto.open = function (method, url) {
      this.__fgMethod = (method || 'GET').toUpperCase();
      this.__fgUrl = url || '';
      this.__fgInternal = false;
      return origOpen.apply(this, arguments);
    };

    XHRProto.setRequestHeader = function (name) {
      if (name && String(name).toLowerCase() === INTERNAL_HEADER.toLowerCase()) {
        this.__fgInternal = true;
      }
      return origSetHeader.apply(this, arguments);
    };

    XHRProto.send = function (body) {
      const xhr = this;
      const url = xhr.__fgUrl || '';
      const method = xhr.__fgMethod || 'GET';

      if (!xhr.__fgInternal && method === 'POST' && CART_WRITE_RE.test(url)) {
        xhr.addEventListener('loadend', () => { sync(); }, { once: true });
      }

      if (!xhr.__fgInternal && method === 'POST' && CART_LOCK_RE.test(url)) {
        const payload = extractPayload(body);
        if (payload) {
          getCart()
            .then((cart) => {
              if (!isChangeBlocked(payload, cart)) return origSend.call(xhr, body);
              const cartJSON = JSON.stringify(cart);
              Object.defineProperty(xhr, 'readyState', { configurable: true, get: () => 4 });
              Object.defineProperty(xhr, 'status', { configurable: true, get: () => 200 });
              Object.defineProperty(xhr, 'responseText', { configurable: true, get: () => cartJSON });
              Object.defineProperty(xhr, 'response', { configurable: true, get: () => cartJSON });
              try { xhr.dispatchEvent(new Event('readystatechange')); } catch (_) {}
              try { xhr.dispatchEvent(new Event('load')); } catch (_) {}
              try { xhr.dispatchEvent(new Event('loadend')); } catch (_) {}
              notifyCart();
            })
            .catch(() => origSend.call(xhr, body));
          return;
        }
      }

      return origSend.apply(this, arguments);
    };
  }

  window.freeGiftCampaigns = {
    campaigns,
    sync,
    inspect: async () => {
      const cart = await getCart();
      await resolveCollections();
      const { states, orphanLines, appliedCodes: applied, fallbackId, cartSubtotal } = evaluate(cart);
      return {
        cart: {
          subtotal: cartSubtotal,
          discountCodes: Array.from(applied),
          attributes: (cart && cart.attributes) || {},
          items: (cart.items || []).map((item) => ({
            variantId: toId(item.variant_id),
            productId: toId(item.product_id),
            quantity: item.quantity,
            subscription: hasSellingPlan(item),
            giftOwner: (item.properties && item.properties[GIFT_PROPERTY]) || null,
          })),
        },
        orphanLines: orphanLines.length,
        campaigns: states.map((state) => ({
          id: state.campaign.id,
          label: state.campaign.label,
          qualifies: state.qualifies,
          giftLines: state.lines.length,
          codeOk: state.codeOk,
          requiredCodes: state.campaign.codes,
          rememberedCodeWins: fallbackId === state.campaign.id,
          triggerLines: state.triggers.length,
          triggerScope: state.campaign.triggerScope,
          triggerProductIds: Array.from(state.campaign.triggerProductIds),
          requireSubscription: state.campaign.requireSubscription,
          minimumMet: state.minimumMet,
          minimumType: state.campaign.minimumType,
          minimumValue: state.campaign.minimumValue,
          triggerSubtotal: state.triggerSubtotal,
          triggerQuantity: state.triggerQuantity,
          failedGiftVariants: state.campaign.giftItems
            .map((gift) => gift.variantId)
            .filter(isVariantFailed),
        })),
      };
    },
  };

  log('initialized', campaigns.map((campaign) => campaign.label));

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', sync);
  } else {
    sync();
  }
  window.addEventListener('pageshow', (event) => {
    if (event.persisted) sync();
  });
})();
